import { useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../home/components/TopNav";

const endpoints = [
  {
    method: "GET",
    path: "/v1/residential/list",
    desc: "获取小区楼栋列表",
    params: ["region_code", "page", "limit"],
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    method: "GET",
    path: "/v1/residential/{id}",
    desc: "获取单个小区详情",
    params: ["id", "fields"],
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    method: "GET",
    path: "/v1/industrial/list",
    desc: "获取工业园区列表",
    params: ["region_code", "industry_type", "page"],
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    method: "GET",
    path: "/v1/commercial/buildings",
    desc: "获取商务楼宇数据",
    params: ["district", "building_type", "limit"],
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    method: "POST",
    path: "/v1/analysis/heatmap",
    desc: "请求区域人口热力分析",
    params: ["bbox", "time_range", "resolution"],
    color: "bg-amber-100 text-amber-700",
  },
  {
    method: "POST",
    path: "/v1/analysis/site-score",
    desc: "选址综合评分计算",
    params: ["lat", "lng", "radius", "industry"],
    color: "bg-amber-100 text-amber-700",
  },
  {
    method: "GET",
    path: "/v1/export/download",
    desc: "下载数据导出文件",
    params: ["task_id", "format"],
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    method: "DELETE",
    path: "/v1/export/task/{id}",
    desc: "删除导出任务",
    params: ["id"],
    color: "bg-rose-100 text-rose-700",
  },
];

const sdks = [
  { lang: "Python", icon: "ri-code-s-slash-line", color: "bg-[#3776ab]/10 text-[#3776ab]", install: "pip install ktsy-sdk" },
  { lang: "JavaScript", icon: "ri-javascript-line", color: "bg-amber-50 text-amber-600", install: "npm install ktsy-js" },
  { lang: "Java", icon: "ri-cup-line", color: "bg-orange-50 text-orange-600", install: "Maven: com.ktsy:ktsy-sdk:2.1.0" },
  { lang: "Go", icon: "ri-terminal-box-line", color: "bg-cyan-50 text-cyan-600", install: "go get github.com/ktsy/ktsy-go" },
];

const plans = [
  {
    name: "开发者版",
    price: "免费",
    unit: "",
    calls: "1,000次/月",
    qps: "5 QPS",
    features: ["基础数据接口", "标准字段返回", "邮件支持"],
    cta: "免费申请",
    highlight: false,
  },
  {
    name: "商业版",
    price: "¥999",
    unit: "/月",
    calls: "50,000次/月",
    qps: "50 QPS",
    features: ["全量数据接口", "完整字段返回", "批量请求", "数据导出接口", "专属客服"],
    cta: "立即开通",
    highlight: true,
  },
  {
    name: "企业版",
    price: "定制",
    unit: "",
    calls: "不限次数",
    qps: "自定义 QPS",
    features: ["专属私有化部署", "白名单IP接入", "定制数据推送", "SLA 99.9%", "数据顾问服务"],
    cta: "联系商务",
    highlight: false,
  },
];

const codeExample = `import ktsy

client = ktsy.Client(api_key="your_api_key_here")

# 获取小区楼栋列表
result = client.residential.list(
    region_code="110105",  # 北京朝阳区
    page=1,
    limit=20
)

for item in result.data:
    print(f"{item.name}: {item.building_count}栋, {item.household_count}户")

# 选址综合评分
score = client.analysis.site_score(
    lat=39.9925,
    lng=116.4631,
    radius=500,
    industry="餐饮"
)
print(f"综合评分: {score.total} | 推荐等级: {score.grade}")`;

export default function ApiServicePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("python");
  const [expandedEndpoint, setExpandedEndpoint] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <TopNav />

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-24 pb-20"
        style={{ background: "linear-gradient(135deg, #0f2460 0%, #1a3a8a 60%, #0e4a7a 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(100,200,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(100,200,255,0.3) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-api-line text-cyan-300 text-sm"></i>
              <span className="text-cyan-300 text-xs font-medium tracking-wide">RESTful API · 空间大数据 · 开箱即用</span>
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">
              API 开放服务
              <span className="block text-xl font-normal text-cyan-300 mt-2">Open Data API Platform</span>
            </h1>
            <p className="text-blue-100/80 text-base leading-relaxed mb-8 max-w-xl">
              通过标准化 RESTful API 接口，将空天数源的空间数据能力无缝集成到您的业务系统中。支持小区楼栋、工业园区、商务楼宇、选址分析等全量数据访问。
            </p>
            <div className="flex items-center gap-4">
              <button className="bg-cyan-400 hover:bg-cyan-300 text-[#0f2460] font-semibold px-8 py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap">
                申请 API Key
              </button>
              <button className="border border-white/30 hover:border-white/60 text-white px-8 py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-book-open-line"></i>查看文档
              </button>
            </div>
            <div className="flex items-center gap-10 mt-10">
              {[["99.9%", "服务可用性"], ["&lt;200ms", "平均响应"], ["30+", "数据接口"], ["日均亿级", "数据调用"]].map(([val, label]) => (
                <div key={label}>
                  <div
                    className="text-2xl font-bold text-cyan-300"
                    dangerouslySetInnerHTML={{ __html: val }}
                  />
                  <div className="text-blue-200/60 text-xs mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Code example */}
      <section className="bg-slate-950 py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center gap-8">
            {/* Left: intro */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-white mb-4">5 行代码，接入完整数据能力</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                提供多语言 SDK，帮助开发者快速完成接入。支持 Python、JavaScript、Java、Go 等主流语言，内置鉴权、错误重试、分页等通用逻辑。
              </p>
              <div className="grid grid-cols-2 gap-3">
                {sdks.map((sdk) => (
                  <div
                    key={sdk.lang}
                    className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 cursor-pointer hover:border-slate-500 transition-colors"
                    onClick={() => setActiveTab(sdk.lang.toLowerCase())}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0 ${sdk.color}`}>
                      <i className={`${sdk.icon} text-base`}></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{sdk.lang} SDK</div>
                      <div className="text-slate-500 text-xs font-mono mt-0.5">{sdk.install}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right: code block */}
            <div className="flex-shrink-0 w-[560px]">
              <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-rose-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
                  </div>
                  <span className="text-slate-400 text-xs font-mono">example.py</span>
                  <button className="text-slate-400 hover:text-white text-xs cursor-pointer flex items-center gap-1">
                    <i className="ri-file-copy-line"></i>复制
                  </button>
                </div>
                <pre className="p-5 text-xs leading-relaxed overflow-x-auto">
                  <code className="text-slate-300 font-mono whitespace-pre">{codeExample}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Endpoint list */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">API 接口一览</h2>
            <p className="text-slate-500 text-sm">全部接口遵循 RESTful 规范，支持 JSON 格式响应</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <span>方法</span>
              <span className="col-span-2">接口路径 &amp; 说明</span>
              <span>参数</span>
            </div>
            {endpoints.map((ep, idx) => (
              <div
                key={idx}
                className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedEndpoint(expandedEndpoint === idx ? null : idx)}
              >
                <div className="grid grid-cols-4 px-5 py-4 items-center">
                  <span className={`inline-flex items-center justify-center w-16 py-0.5 rounded text-xs font-bold font-mono ${ep.color}`}>
                    {ep.method}
                  </span>
                  <div className="col-span-2">
                    <div className="font-mono text-sm text-slate-800">{ep.path}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{ep.desc}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {ep.params.slice(0, 2).map((p) => (
                        <span key={p} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono">{p}</span>
                      ))}
                      {ep.params.length > 2 && (
                        <span className="text-xs text-slate-400">+{ep.params.length - 2}</span>
                      )}
                    </div>
                    <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${expandedEndpoint === idx ? "rotate-180" : ""}`}></i>
                  </div>
                </div>
                {expandedEndpoint === idx && (
                  <div className="px-5 pb-4 bg-slate-50 border-t border-slate-100">
                    <div className="text-xs text-slate-500 mb-2 font-semibold">完整参数列表：</div>
                    <div className="flex flex-wrap gap-2">
                      {ep.params.map((p) => (
                        <span key={p} className="text-xs bg-white border border-slate-200 text-slate-700 px-2.5 py-1 rounded font-mono">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auth & security */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-3 gap-8">
            {[
              {
                icon: "ri-key-2-line",
                title: "API Key 鉴权",
                desc: "通过 HTTP Header 传入 X-API-Key 完成鉴权，支持多个 Key 分场景使用，可随时撤销。",
                color: "bg-cyan-50 text-cyan-600",
              },
              {
                icon: "ri-shield-check-line",
                title: "HTTPS 加密传输",
                desc: "全程 TLS 1.3 加密，数据在传输过程中无法被截获或篡改，保障业务数据安全。",
                color: "bg-emerald-50 text-emerald-600",
              },
              {
                icon: "ri-speed-line",
                title: "限流 &amp; 熔断机制",
                desc: "内置请求限流（QPS 控制），超限自动返回 429，搭配指数退避重试策略，稳定可靠。",
                color: "bg-amber-50 text-amber-600",
              },
            ].map((item) => (
              <div key={item.title} className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${item.color}`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-slate-900 font-bold text-base mb-2" dangerouslySetInnerHTML={{ __html: item.title }} />
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">API 调用套餐</h2>
            <p className="text-slate-500 text-sm">按月计费，随时升降级，无需合同</p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl border overflow-hidden ${plan.highlight ? "border-cyan-400 ring-2 ring-cyan-200" : "border-slate-200 bg-white"}`}
              >
                {plan.highlight && <div className="h-1 bg-gradient-to-r from-cyan-400 to-blue-500"></div>}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-slate-800">{plan.name}</h3>
                    {plan.highlight && <span className="text-xs bg-cyan-500 text-white px-2 py-0.5 rounded-full font-semibold">推荐</span>}
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className={`text-3xl font-bold ${plan.highlight ? "text-cyan-600" : "text-slate-800"}`}>{plan.price}</span>
                    <span className="text-sm text-slate-500">{plan.unit}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-5 mt-2">
                    <span className="flex items-center gap-1"><i className="ri-exchange-line"></i>{plan.calls}</span>
                    <span className="flex items-center gap-1"><i className="ri-flashlight-line"></i>{plan.qps}</span>
                  </div>
                  <button
                    className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${plan.highlight ? "bg-cyan-500 hover:bg-cyan-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-800"}`}
                  >
                    {plan.cta}
                  </button>
                </div>
                <div className="px-6 pb-6 space-y-2 border-t border-slate-100 pt-4">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-check-line text-sm ${plan.highlight ? "text-cyan-500" : "text-slate-400"}`}></i>
                      </div>
                      <span className="text-xs text-slate-600">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer area */}
      <footer className="bg-slate-900 py-10">
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-earth-fill text-cyan-300 text-xl"></i>
            <span className="text-white font-bold">空天数源</span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white text-sm cursor-pointer">首页</button>
            <button onClick={() => navigate("/products/enterprise-insight")} className="text-slate-400 hover:text-white text-sm cursor-pointer">产品服务</button>
            <button onClick={() => navigate("/api-service")} className="text-slate-400 hover:text-white text-sm cursor-pointer">API服务</button>
          </div>
          <p className="text-slate-500 text-xs">&copy; 2024 空天数源. 保留所有权利。</p>
        </div>
      </footer>
    </div>
  );
}
